import type { ReactNode } from "react";
import { Group, Paper, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Alert } from "./alert";
import { Spinner } from "./spinner";

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm" mb="md">
      <div>
        <Title order={1} size="h1">
          {title}
        </Title>
        {description ? (
          <Text size="sm" c="dimmed" mt={4}>
            {description}
          </Text>
        ) : null}
      </div>
      {action}
    </Group>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation("common");
  const text = label ?? t("loading");

  return (
    <Paper withBorder radius="md" px="md" py="xl">
      <Group gap="sm">
        <Spinner label={text} />
        <Text size="sm" c="dimmed" aria-hidden="true">
          {text}
        </Text>
      </Group>
    </Paper>
  );
}

/**
 * The same failure card everywhere, so a screen that cannot load says why
 * instead of rendering as if there were simply no data.
 */
export function ErrorBlock({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation("common");
  const message = error instanceof Error ? error.message : t("loadFailed");

  return (
    <Alert>
      <p>{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 text-sm font-medium underline underline-offset-2"
        >
          {t("retry")}
        </button>
      ) : null}
    </Alert>
  );
}
